/**
 * readAloud/client — one call per segment, authenticated as the reader.
 *
 * Same transport idiom as src/services/prest0nSession/client.ts: the Cloud
 * Functions base URL, a Firebase ID token as a Bearer header, and a timeout so
 * a hung request can never wedge the player's state machine.
 *
 * ONE CALL PER SEGMENT is the design, not an accident. The first response
 * carries `segmentCount`, so the player can start playing segment 0 while it
 * fetches 1 and 2 behind it — which is what makes a long answer start reading
 * in a second or two instead of after the whole thing has rendered. It also
 * means every segment is independently cacheable and independently resumable.
 */

import { getAuth } from 'firebase/auth';

const CF_BASE_URL = 'https://us-central1-tensile-tenure-453409-k3.cloudfunctions.net';

/** A render can legitimately take a few seconds; past this something is wrong. */
const READ_ALOUD_TIMEOUT_MS = 60_000;

export interface ReadAloudSegment {
  index: number;
  url: string;
  characters: number;
  durationSeconds?: number;
}

export interface ReadAloudResponse {
  ok: boolean;
  segmentCount: number;
  segment?: ReadAloudSegment;
  cached: boolean;
  truncated?: boolean;
  reason?: string;
  notice?: string;
}

export interface ReadAloudRequest {
  threadId: string;
  messageId: string;
  segment: number;
  /** What the reader is looking at — used ONLY if the store has no such message. */
  fallbackText?: string;
  language?: string;
  signal?: AbortSignal;
}

/** The transport gave up or never started. Never a claim about the message. */
const TRANSPORT_FAILURE: ReadAloudResponse = {
  ok: false,
  segmentCount: 0,
  cached: false,
  reason: 'transport',
};

export async function fetchReadAloudSegment(req: ReadAloudRequest): Promise<ReadAloudResponse> {
  const user = getAuth().currentUser;
  if (!user) return { ...TRANSPORT_FAILURE, reason: 'unauthenticated' };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), READ_ALOUD_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  req.signal?.addEventListener('abort', onAbort, { once: true });

  try {
    const token = await user.getIdToken(false);
    const response = await fetch(`${CF_BASE_URL}/prest0nReadAloud`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        threadId: req.threadId,
        messageId: req.messageId,
        segment: req.segment,
        ...(req.fallbackText ? { fallbackText: req.fallbackText } : {}),
        ...(req.language ? { language: req.language } : {}),
      }),
      signal: controller.signal,
    });
    // Every refusal this endpoint makes is a JSON body with its own notice, so
    // a non-2xx is read the same way as a success — the status only decides
    // whether `ok` is true.
    const body = (await response.json().catch(() => null)) as ReadAloudResponse | null;
    if (!body || typeof body !== 'object') return TRANSPORT_FAILURE;
    return { ...body, ok: body.ok === true && response.ok };
  } catch {
    return TRANSPORT_FAILURE;
  } finally {
    clearTimeout(timer);
    req.signal?.removeEventListener('abort', onAbort);
  }
}
